/**
 * Optional Sentry frontend observability.
 * Disabled when REACT_APP_SENTRY_DSN is unset. Never logs secrets or PII.
 */

import React from 'react';
import * as Sentry from '@sentry/react';

const SENSITIVE_HEADER_KEYS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
]);

let initialized = false;

/**
 * Read Sentry env config from CRA process.env (or test override).
 */
export function readSentryEnvConfig(env = process.env) {
  const dsn = String(env.REACT_APP_SENTRY_DSN || '').trim();
  const environment = String(env.REACT_APP_SENTRY_ENVIRONMENT || '').trim();
  const release = String(env.REACT_APP_SENTRY_RELEASE || '').trim();

  return {
    dsn,
    environment: environment || undefined,
    release: release || undefined,
  };
}

/**
 * True when a DSN is configured and Sentry may initialize.
 */
export function isSentryEnabled(config = readSentryEnvConfig()) {
  return Boolean(config?.dsn);
}

function scrubRequest(event) {
  if (!event?.request) return;

  if (event.request.headers && typeof event.request.headers === 'object') {
    const headers = { ...event.request.headers };
    for (const key of Object.keys(headers)) {
      if (SENSITIVE_HEADER_KEYS.has(key.toLowerCase())) {
        delete headers[key];
      }
    }
    event.request.headers = headers;
  }

  delete event.request.cookies;
  delete event.request.data;
  delete event.request.body;
}

function scrubUser(event) {
  if (!event?.user) return;
  delete event.user.email;
  delete event.user.username;
  delete event.user.name;
  delete event.user.ip_address;
}

/**
 * beforeSend hook — drop PII and sensitive request material.
 */
export function sentryBeforeSend(event) {
  if (!event || typeof event !== 'object') return null;

  scrubUser(event);
  scrubRequest(event);

  return event;
}

/**
 * Initialize Sentry when DSN is present. Safe no-op otherwise.
 */
export function initSentry(env = process.env) {
  if (initialized) return false;

  const config = readSentryEnvConfig(env);
  if (!isSentryEnabled(config)) return false;

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      sendDefaultPii: false,
      beforeSend: sentryBeforeSend,
      tracesSampleRate: 0,
    });
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

function SentryFallback() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-lg font-medium text-white mb-2">Something went wrong</h1>
        <p className="text-sm text-slate-400">
          Please refresh the page or try again later.
        </p>
      </div>
    </div>
  );
}

/**
 * Error boundary when Sentry is enabled; passthrough children otherwise.
 */
export function SentryAppBoundary({ children }) {
  if (!isSentryEnabled()) {
    return children;
  }

  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>{children}</Sentry.ErrorBoundary>
  );
}
